import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import CustomHeaderTitleView from "presentational/common/CustomHeaderTitleView";
import ImageCropperPanel from "container/upload/ImageCropperPanel";
import StickerAdderPanel from "container/upload/StickerAdderPanel";
import ItemAdderPanel from "container/upload/ItemAdderPanel";
import DateSetUpPanel from "container/upload/DateSetUpPanel";
import OutfitSummaryPanel from "container/upload/OutfitSummaryPanel";
import RootItemUpload from "container/upload/RootItemUpload";
import LoadingModalView from "presentational/common/modals/LoadingModalView";
import TXT from "presentational/common/multinational/TXT";
import { uploadImage } from "lib/imageUploadHelper";
import { withToken } from "lib/forceTokenRenewComponent";
import { withStore } from "store/storeHandler";

const INIT_STICKER_DATA = {
  percentX: 0.5,
  percentY: 0.12,
  visible: true,
  tooltipOpen: true
};

const getNowCalendarDate = () => {
  const date = new Date();
  const fullYear = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${fullYear}-${month}-${day}`;
};

function RootOutfitUpload(props) {
  const { MyOutfitActions, AppErrorActions, NoticeModalActions } = props;
  const { forceRenewTokenWT, getIdTokenWT } = props;
  const [croppedImageDataURL, setCroppedImageDataURL] = useState(null);
  const [stickeredImageDataURL, setStickeredImageDataURL] = useState(null);
  const [internalCropData, setInternalCropData] = useState(null);
  const [internalStickerData, setInternalStickerData] =
    useState(INIT_STICKER_DATA);
  const [itemList, setItemList] = useState([]);
  const [calendarDate, setCalendarDate] = useState(getNowCalendarDate());
  const [calendarAgreement, setCalendarAgreement] = useState(true);
  const [phase, setPhase] = useState("imageCrop");
  const [
    hasAddImageButtonClickedAtLeastOnce,
    setHasAddImageButtonClickedAtLeastOnce
  ] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const history = useHistory();

  const handleImageCrop = (newCroppedImageDataURL, internalCropData) => {
    if (croppedImageDataURL !== newCroppedImageDataURL) {
      setInternalStickerData(INIT_STICKER_DATA);
    }
    setCroppedImageDataURL(newCroppedImageDataURL);
    setInternalCropData(internalCropData);
  };

  const handleStickerChange = internalStickerData => {
    setInternalStickerData(internalStickerData);
  };

  const handleStickeredImageChange = stickeredImageDataURL => {
    setStickeredImageDataURL(stickeredImageDataURL);
  };

  const handleItemSelect = itemList => {
    setItemList(itemList);
  };

  const goToImageCropPhase = () => {
    setPhase("imageCrop");
  };

  const goToStickerAddPhase = () => {
    setPhase("stickerAdd");
  };

  const goToItemAddPhase = () => {
    setPhase("itemAdd");
  };

  const goToDateSetPhase = () => {
    setPhase("dateSet");
  };

  const goToSummaryPhase = () => {
    setPhase("summary");
  };

  const handleUploadBtnClick = () => {
    setPhase("itemUpload");
  };

  const handleSubmitClick = async () => {
    const itemHashArray = itemList.map(itemObj => itemObj.itemHash);

    if (isUploading) return null;

    await setIsUploading(true);

    const hasSucceed = await forceRenewTokenWT();
    if (!hasSucceed) {
      setIsUploading(false);
      return null;
    }

    const { idToken, hasTokenExpired } = await getIdTokenWT();
    if (hasTokenExpired) {
      setIsUploading(false);
      return null;
    }

    let filename;
    try {
      filename = await uploadImage("outfit", stickeredImageDataURL, idToken);
    } catch (error) {
      setIsUploading(false);
      AppErrorActions.setSafeAppError(error);
      NoticeModalActions.openModal({
        content: <TXT name="msg_outfitUploadError" />
      });
      return null;
    }

    try {
      await MyOutfitActions.uploadOutfit({
        filename,
        itemHashArray,
        calendarDate,
        calendarAgreement,
        idToken
      });
    } catch (error) {
      setIsUploading(false);
      AppErrorActions.setSafeAppError(
        Error(`rollback problem => unlinkedOutfitImageFile: "${filename}"`)
      );
      AppErrorActions.setFatalAppError(error);
      return null;
    }
    MyOutfitActions.deleteAllData();
    MyOutfitActions.setRealtimeDataOnNextRequest();
    setIsUploading(false);
    goBack();
  };

  const goBack = () => {
    history.goBack();
  };

  const handleAddImageBtnClick = () => {
    setHasAddImageButtonClickedAtLeastOnce(true);
  };

  const handleCalendarDateChange = dateStr => {
    setCalendarDate(dateStr);
  };

  const handleCalendarAgreementChange = inputBool => {
    setCalendarAgreement(inputBool);
  };

  return (
    <div>
      <CustomHeaderTitleView
        title={<TXT name="titleImgUpload_outfit" />}
        displayOnMobile
        displayOnDesktop
      />
      {phase === "imageCrop" && (
        <ImageCropperPanel
          imageType="outfit"
          internalCropData={internalCropData}
          resultImageQuality={1}
          onImageCrop={handleImageCrop}
          onCancelClick={goBack}
          onNextClick={goToStickerAddPhase}
          hasAddImageButtonClickedAtLeastOnce={
            hasAddImageButtonClickedAtLeastOnce
          }
          onAddImageBtnClick={handleAddImageBtnClick}
        />
      )}
      {phase === "stickerAdd" && (
        <StickerAdderPanel
          imageDataURL={croppedImageDataURL}
          internalStickerData={internalStickerData}
          tooltipText={<TXT name="msg_dragSticker" />}
          onStickerChange={handleStickerChange}
          onStickeredImageChange={handleStickeredImageChange}
          onBackClick={goToImageCropPhase}
          onNextClick={goToItemAddPhase}
        />
      )}
      {phase === "itemAdd" && (
        <ItemAdderPanel
          mode="outfitUpload"
          stickeredImageDataURL={stickeredImageDataURL}
          selectedItemList={itemList}
          onBackClick={goToStickerAddPhase}
          onNextClick={goToDateSetPhase}
          onItemUploadBtnClick={handleUploadBtnClick}
          onItemSelect={handleItemSelect}
        />
      )}
      {phase === "dateSet" && (
        <DateSetUpPanel
          stickeredImageDataURL={stickeredImageDataURL}
          calendarDate={calendarDate}
          calendarAgreement={calendarAgreement}
          onCalendarDateChange={handleCalendarDateChange}
          onCalendarAgreementChange={handleCalendarAgreementChange}
          onBackClick={goToItemAddPhase}
          onNextClick={goToSummaryPhase}
        />
      )}
      {phase === "summary" && (
        <OutfitSummaryPanel
          stickeredImageDataURL={stickeredImageDataURL}
          itemList={itemList}
          calendarDate={calendarDate}
          calendarAgreement={calendarAgreement}
          onBackClick={goToDateSetPhase}
          onSubmitClick={handleSubmitClick}
        />
      )}
      {phase === "itemUpload" && (
        <RootItemUpload
          alreadyLoadedImageData={{
            originalImageElem: internalCropData.originalImageElem,
            originalImageDataURL: internalCropData.originalImageDataURL
          }}
          onExitClick={goToItemAddPhase}
        />
      )}
      <LoadingModalView open={isUploading} />
    </div>
  );
}

export default withStore({
  dispatchMap: ["myOutfit", "appError", "noticeModal"]
})(withToken(RootOutfitUpload));
